import React, { useMemo } from 'react'
import InputField, { InputFieldProps } from './InputField'
import { SxProps, TextFieldProps, Theme } from '@mui/material'
import SwitchInput, { SwitchInputProps } from './SwitchInput'
import SelectField, { SelectFieldProps } from './SelectField'
import { useEditionMode, EditionModes } from '../../utils/providers/editionModeContext'
import DateField, { DateFieldProps } from './DateField'
import RadioButtonsGroup, { RadioButtonsGroupProps } from './RadioButtonsGroup'
import AutoCompleteGoogleMapField from './autoCompleteGoogleMapField'
import ReadOnlyAutoCompleteGoogleMapField from './autoCompleteGoogleMapField/ReadOnlyAutoCompleteGoogleMapField'
import { AutoCompleteGoogleMapFieldProps } from './autoCompleteGoogleMapField/intefaces'
import InputFieldArray, { InputFieldArrayProps } from './InputFieldArray'
import { useHoverEditionHandlers, withHoverEditionMode } from '../../utils/withHoverEditionMode'
import DisplayedItem, { DisplayedItemProps } from './DisplayedItem'
import { useUserPermissions } from '../../pages/company/userPermissions'
import IabAutoCompleteField, { IABAutoCompleteFieldProps } from './IABAutoCompleteField'
import { LocationTypesEnum } from '../../pages/adgroup/settings/geoTargeting/GeoTargetingFields'
import { uppercaseFieldLabelSx } from './styled'

export const labelStyle = {
  color: 'text.secondary',
  textTransform: 'uppercase',
  fontSize: 10.5,
} as const

export const valueStyle = {
  overflow: 'auto',
  display: 'flex',
  alignItems: 'center',
  pt: '3px',
  pb: '5px',
  lineHeight: '1.9rem',
  fontSize: '1.4rem',
  '& a': {
    color: 'primary.main',
    textDecoration: 'none',
    '&:hover': {
      color: 'primary.main',
      textDecoration: 'none',
    },
    '&:visited': {
      color: 'primary.main',
      textDecoration: 'none',
    },
  },
} as const

interface DisplayProps extends Partial<DisplayedItemProps> {
  inputType: string
  label: string
  name: string
  text: string | null | undefined
  placeholder?: string
  tooltipText?: string
  isLink?: boolean
  editable?: boolean
  permission?: 'read' | 'edit' | 'admin'
  toDisplay?: JSX.Element
  disableHoverEdition?: boolean
  sx?: SxProps<Theme>
  required?: boolean
  showErrorOnInit?: boolean
}

interface InputProps extends DisplayProps, Omit<Partial<InputFieldProps>, 'label' | 'name'> {
  inputType: 'input'
  type: 'text' | 'number' | 'email' | 'password' | 'url'
  InputProps?: TextFieldProps['InputProps']
  InputLabelProps?: TextFieldProps['InputLabelProps']
  multiline?: boolean
}

interface InputArrayProps extends DisplayProps, Omit<Partial<InputFieldArrayProps>, 'label' | 'name'> {
  inputType: 'inputFieldArray'
  type: 'text' | 'number' | 'email' | 'password' | 'url'
  InputProps?: TextFieldProps['InputProps']
  InputLabelProps?: TextFieldProps['InputLabelProps']
}

interface DatePickerProps extends DisplayProps, Omit<Partial<DateFieldProps>, 'label' | 'name'> {
  inputType: 'datepicker'
  InputProps?: TextFieldProps['InputProps']
  InputLabelProps?: TextFieldProps['InputLabelProps']
  clearable?: boolean
}

interface SwitchProps extends DisplayProps, Omit<Partial<SwitchInputProps>, 'label' | 'name'> {
  inputType: 'switch'
  textToggle?: { true: string; false: string }
  testId?: string
  disabled?: boolean
}

interface SelectProps extends DisplayProps, Omit<Partial<SelectFieldProps>, 'label' | 'name'> {
  inputType: 'select'
}

interface RadioProps extends DisplayProps, Omit<Partial<RadioButtonsGroupProps>, 'label' | 'name'> {
  inputType: 'radio'
}

interface MapProps extends DisplayProps, Omit<Partial<AutoCompleteGoogleMapFieldProps>, 'label' | 'name'> {
  inputType: 'autoCompleteGoogleMap'
  locationType?: LocationTypesEnum
  countryCode?: string
}

interface IabAutoCompleteProps extends DisplayProps, Omit<Partial<IABAutoCompleteFieldProps>, 'label' | 'name'> {
  inputType: 'iabAutoComplete'
  withAllOption?: boolean
}

export type TransformDisplayToEditionProps =
  | InputProps
  | DatePickerProps
  | InputArrayProps
  | SwitchProps
  | SelectProps
  | RadioProps
  | MapProps
  | IabAutoCompleteProps

const getEditionFieldVariant = (compact: boolean, disableHoverEdition: boolean): 'filled' | 'outlined' | 'standard' => {
  if (compact) return 'standard'
  return disableHoverEdition ? 'outlined' : 'standard'
}

const getEditionFieldSx = (compact: boolean): SxProps<Theme> | undefined =>
  compact ? undefined : uppercaseFieldLabelSx

const TransformDisplayToEdition: React.FC<TransformDisplayToEditionProps> = ({
  text,
  label,
  inputType = 'input',
  editable = true,
  name,
  children,
  tooltipText,
  disableHoverEdition = false,
  permission = 'edit',
  placeholder = 'n/a',
  toDisplay,
  required,
  showErrorOnInit,
  compact = false,
  ...props
}) => {
  const [editionModeContext, setEditionMode] = useEditionMode()
  const { setDisplayOnLeave, setEditionOnEnter, handleFocus, handleBlur, handleOpen, handleClose } =
    useHoverEditionHandlers(name, disableHoverEdition)
  const { read, edit, admin } = useUserPermissions()

  const canEdit = useMemo(() => {
    if (editable)
      switch (permission) {
        case 'read':
          return read
        case 'edit':
          return edit
        case 'admin':
          return admin
      }

    return false
  }, [editable, read, edit, admin])

  const editionFieldVariant = getEditionFieldVariant(compact, disableHoverEdition)
  const editionFieldSx = getEditionFieldSx(compact)

  if (!canEdit || editionModeContext === EditionModes.Display) {
    if (inputType === 'autoCompleteGoogleMap') {
      return (
        <ReadOnlyAutoCompleteGoogleMapField
          name={name}
          label={label}
          locationType={(props as MapProps).locationType}
          compact={compact}
          onMouseEnter={canEdit ? setEditionOnEnter : undefined}
          onMouseLeave={canEdit ? setDisplayOnLeave : undefined}
          sx={editionFieldSx}
        />
      )
    }

    return (
      <DisplayedItem
        label={label}
        isLink={props.isLink}
        tooltipText={tooltipText}
        name={name}
        onMouseEnter={canEdit ? setEditionOnEnter : undefined}
        onMouseLeave={canEdit ? setDisplayOnLeave : undefined}
        disableHoverEdition={disableHoverEdition}
        editable={canEdit}
        toDisplay={toDisplay}
        compact={compact}
      >
        {text ?? placeholder}
      </DisplayedItem>
    )
  } else
    switch (inputType) {
      case 'input':
        return (
          <InputField
            name={name}
            placeholder={placeholder}
            label={label}
            required={required}
            sx={editionFieldSx}
            onMouseLeave={setDisplayOnLeave}
            tooltipText={tooltipText}
            variant={editionFieldVariant}
            defaultHelperText={(props as InputProps).defaultHelperText ?? ' '}
            onFocus={handleFocus}
            onBlur={handleBlur}
            type={(props as InputProps).type}
            multiline={(props as InputProps).multiline}
            rows={(props as InputProps).rows}
            InputProps={(props as InputProps).InputProps}
            InputLabelProps={(props as InputProps).InputLabelProps}
            onChange={(props as InputProps).onChange}
            showErrorOnInit={showErrorOnInit}
            compact={compact}
          />
        )
      case 'switch':
        return (
          <SwitchInput
            name={name}
            label={label}
            onMouseLeave={setDisplayOnLeave}
            textToggle={(props as SwitchProps).textToggle}
            compact={compact}
            testId={(props as SwitchProps).testId}
            disabled={(props as SwitchProps).disabled}
            sx={(props as SwitchProps).sx}
          />
        )
      case 'select':
        return (
          <SelectField
            required={required}
            name={name}
            label={label}
            sx={editionFieldSx}
            onMouseLeave={setDisplayOnLeave}
            onOpen={handleOpen}
            onClose={handleClose}
            tooltipText={tooltipText}
            variant={editionFieldVariant}
            placeholder={text || undefined}
            defaultHelperText=" "
            withNone={(props as SelectProps).withNone}
            noneTranslation={(props as SelectProps).noneTranslation}
            multiple={(props as SelectProps).multiple}
            options={(props as SelectProps).options}
            compact={compact}
            disabled={(props as SelectProps).disabled}
          >
            {children}
          </SelectField>
        )
      case 'datepicker':
        return (
          <DateField
            name={name}
            label={label}
            required={required}
            onMouseLeave={setDisplayOnLeave}
            sx={editionFieldSx}
            disableHoverEdition={disableHoverEdition}
            setEditionMode={setEditionMode}
            tooltipText={tooltipText}
            placeholder={placeholder ?? 'n/a'}
            variant={editionFieldVariant}
            defaultHelperText=" "
            InputProps={(props as DatePickerProps).InputProps}
            InputLabelProps={(props as DatePickerProps).InputLabelProps}
            inputFormat={(props as DatePickerProps).inputFormat}
            minDate={(props as DatePickerProps).minDate}
            clearable={(props as DatePickerProps).clearable}
            compact={compact}
          />
        )
      case 'radio':
        return (
          <RadioButtonsGroup
            label={label}
            name={name}
            onMouseLeave={setDisplayOnLeave}
            options={(props as RadioProps).options}
            compact={compact}
          />
        )
      case 'autoCompleteGoogleMap':
        return (
          <AutoCompleteGoogleMapField
            name={name}
            label={label}
            locationType={(props as MapProps).locationType}
            countryCode={(props as MapProps).countryCode}
            compact={compact}
          />
        )
      case 'inputFieldArray':
        return (
          <InputFieldArray
            name={name}
            label={label}
            required={required}
            onMouseLeave={setDisplayOnLeave}
            type={(props as InputArrayProps).type}
            InputLabelProps={(props as InputArrayProps).InputLabelProps}
            showErrorOnInit={showErrorOnInit}
            compact={compact}
          />
        )
      case 'iabAutoComplete':
        return (
          <IabAutoCompleteField
            label={label}
            name={name}
            required={required}
            onMouseLeave={setDisplayOnLeave}
            variant={editionFieldVariant}
            sx={editionFieldSx}
            onFocus={handleFocus}
            onBlur={handleBlur}
            withAllOption={(props as IabAutoCompleteProps).withAllOption}
            compact={compact}
          />
        )
    }
}

export default withHoverEditionMode(TransformDisplayToEdition)
